// services/bucketFilterService.js
// Centralized service for managing application bucket filters across different roles

import { doc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from './firebaseConfig';

/**
 * Determine doctor bucket based on application state
 * @param {Object} application - Application data
 * @returns {string} - Doctor bucket filter value
 */
export const determineDoctorBucket = (application) => {
  const w = application?.workflow || {};
  const mv = w.medicalVerification || {};
  const pv = w.policyVerification || {};
  const ds = w.dischargeSettlement || {};
  const dispatch = w.dispatch || {};
  const disb = w.disbursal || {};

  // Query flags
  const hasUnresolvedQueries = application?.queryStatus?.hasUnresolvedQueries || application?.status === 'dispute_pending';
  const queryReplyReceived = application?.queryReplies?.hasHospitalReply;
  const diagnosisApprovalPending = application?.queryReplies?.hasAcceptedReply && mv?.status === 'pending';

  // KYP flags
  const kypAckStatus = mv?.kypHospitalAcknowledgement?.status;
  const kypGenerated = mv?.kypGenerated;
  const kypAccepted = kypAckStatus === 'accepted';
  const kypChallenged = kypAckStatus === 'challenged';
  const kypRejected = kypAckStatus === 'rejected';

  // FAL flags
  const falGenerated = !!ds?.finalApprovalLetterUrl;
  const falAckStatus = ds?.hospitalAcknowledgement?.status;
  const sanctionsChallenged = Array.isArray(w?.sanctions) && w.sanctions.some(s => s?.hospitalAcknowledgement?.status === 'challenged');
  const sanctionsAccepted = Array.isArray(w?.sanctions) && w.sanctions.some(s => s?.hospitalAcknowledgement?.status === 'accepted');
  const falAccepted = falAckStatus === 'accepted' || sanctionsAccepted;
  const falChallenged = falAckStatus === 'challenged' || sanctionsChallenged;

  // Discharge flags
  const dischargeInitiated = application?.dischargeInitiated;
  const hasUploadedDischargeDocs = Array.isArray(ds?.uploadedDocuments) && ds.uploadedDocuments.length > 0;
  const hasDispatch = !!dispatch?.status;

  // Rejection flags
  const isRejected = application?.status === 'rejected' || application?.status === 'medical_rejected' || kypRejected;

  // 1. Query Raised - highest priority
  if (hasUnresolvedQueries) return 'query_raised';

  // 2. Rejected with Challenge
  if (isRejected && (kypChallenged || falChallenged)) return 'rejected_challenge';

  // 3. Rejected (general)
  if (isRejected) return 'rejected';

  // 4. Diagnosis Approval Pending (query reply accepted, awaiting approval)
  if (diagnosisApprovalPending) return 'diagnosis_approval_pending';

  // 5-7. Query Reply Received (contextual based on phase)
  if (queryReplyReceived) {
    // In KYP phase
    if (!kypGenerated) return 'kyp_query_reply_received';
    // In discharge phase
    if (kypAccepted && !falAccepted) return 'discharge_query_reply_received';
    // In scrutiny phase
    if (falAccepted) return 'scrutiny_query_reply_received';
  }

  // 8. KYP Challenged
  if (kypChallenged) return 'kyp_challenged';

  // 9. FAL Challenged
  if (falChallenged) return 'fal_challenged';

  // 10. KYP Pending (policy ready, KYP not generated)
  const policyReady = pv?.status === 'completed' || pv?.status === 'approved';
  if (policyReady && !kypGenerated && !isRejected) return 'kyp_pending';

  // 11. KYP Approved (accepted, discharge not started)
  if (kypAccepted && !dischargeInitiated && !hasUploadedDischargeDocs) return 'kyp_approved';

  // 12. Discharge Pending (discharge started, FAL not generated)
  if ((dischargeInitiated || hasUploadedDischargeDocs) && !falGenerated) return 'discharge_pending';

  // 13. Discharge Approved (FAL generated, not accepted)
  if (falGenerated && !falAccepted) return 'discharge_approved';

  // 14. Scrutiny Pending (FAL accepted, no dispatch)
  if (falAccepted && !hasDispatch && !disb?.status) return 'scrutiny_pending';

  // 15. Scrutiny Approved (dispatch started)
  if (falAccepted && hasDispatch && !disb?.status) return 'scrutiny_approved';

  // 16. FAL Approved (fallback for accepted FAL)
  if (falAccepted) return 'fal_approved';

  // 17. New (default)
  return 'new';
};

/**
 * Determine finance bucket based on application state
 * @param {Object} application - Application data
 * @returns {string|null} - Finance bucket filter value (null if not in finance pipeline)
 */
export const determineFinanceBucket = (application) => {
  const w = application?.workflow || {};
  const fin = w.financialVerification || {};
  const ds = w.dischargeSettlement || {};
  const dispatch = w.dispatch || {};
  const disb = w.disbursal || {};
  const enach = w.enach || w.eNach || {};
  const recovery = w.recovery || {};
  const pod = w.proofOfDispatch || w.pod || {};

  // Not in finance pipeline yet
  const medicalApproved = application?.status === 'medical_approved' || 
                          w.medicalVerification?.status === 'approved' ||
                          w.medicalVerification?.kypGenerated;
  
  if (!medicalApproved && !fin?.status) return null;

  // 1. Query
  if (application?.status === 'dispute_pending' || application?.status === 'pending_financial_clarification') {
    return 'query';
  }

  // 2. Claim Follow-ups
  if (recovery?.pendingTasks || recovery?.followUpsPending || recovery?.status === 'follow_up') {
    return 'claimFollowUps';
  }

  // 3. Disbursement
  if (disb?.status || disb?.finalAmount || disb?.letterGenerated) {
    return 'disbursement';
  }

  // 4. POD (Proof of Dispatch)
  if (pod?.status || pod?.trackingNumber || pod?.acknowledgedAt) {
    return 'pod';
  }

  // 5. eNach Completed
  if (enach?.status === 'completed' || enach?.completedAt) {
    return 'enachCompleted';
  }

  // 6. FAL Accepted
  const falAccepted = ds?.hospitalAcknowledgement?.status === 'accepted' ||
                      (Array.isArray(w?.sanctions) && w.sanctions.some(s => s?.hospitalAcknowledgement?.status === 'accepted'));
  if (falAccepted) {
    return 'falAccepted';
  }

  // 7. Scrutiny (docs uploaded, no dispatch)
  const hasUploadedDocs = Array.isArray(ds?.uploadedDocuments) && ds.uploadedDocuments.length > 0;
  const hasDispatch = !!dispatch?.status;
  if (hasUploadedDocs && !hasDispatch) {
    return 'scrutiny';
  }

  // 8. Financial Approved
  if (fin?.status === 'approved' || fin?.approvedAt) {
    return 'financialApproved';
  }

  // 9. New (default for finance pipeline)
  return 'new';
};

/**
 * Determine policy bucket based on application state
 * @param {Object} application - Application data
 * @returns {string} - Policy bucket filter value
 */
export const determinePolicyBucket = (application) => {
  const pv = application?.workflow?.policyVerification || {};

  if (pv?.status === 'rejected') return 'rejected';
  if (pv?.status === 'completed' || pv?.status === 'approved') return 'approved';
  
  return 'pending';
};

/**
 * Determine hospital bucket based on application state
 * @param {Object} application - Application data
 * @returns {string} - Hospital bucket filter value
 */
export const determineHospitalBucket = (application) => {
  const status = application?.status;
  const w = application?.workflow || {};

  // Rejected
  if (status === 'rejected' || status === 'medical_rejected' || status === 'fal_rejected') {
    return 'rejected';
  }

  // Approved (fully processed)
  const falAccepted = w.dischargeSettlement?.hospitalAcknowledgement?.status === 'accepted';
  if (status === 'approved' || falAccepted) {
    return 'approved';
  }

  // Under Review
  const underReview = status === 'under_review' ||
                      w.medicalVerification?.status === 'in_progress' ||
                      w.financialVerification?.status === 'in_progress' ||
                      status === 'dispute_pending';
  if (underReview) {
    return 'under_review';
  }

  // Submitted (default)
  return 'submitted';
};

/**
 * Calculate complete bucket filter for an application
 * @param {Object} application - Application data
 * @returns {Object} - Complete bucketFilter object
 */
export const calculateBucketFilter = (application) => {
  return {
    doctor: determineDoctorBucket(application),
    finance: determineFinanceBucket(application),
    policy: determinePolicyBucket(application),
    hospital: determineHospitalBucket(application),
    admin: 'all' // Admin always sees all
  };
};

/**
 * Update bucket filter in Firestore
 * @param {string} applicationDocId - Firestore document ID
 * @param {Object} application - Current application data
 * @returns {Promise<Object>} - Result object
 */
export const updateBucketFilter = async (applicationDocId, application) => {
  try {
    const bucketFilter = calculateBucketFilter(application);
    
    const appRef = doc(db, 'patientApplications', applicationDocId);
    await updateDoc(appRef, {
      bucketFilter,
      'bucketFilter.updatedAt': Timestamp.now()
    });

    return {
      success: true,
      bucketFilter
    };
  } catch (error) {
    console.error('Error updating bucket filter:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Update specific role bucket filter
 * @param {string} applicationDocId - Firestore document ID
 * @param {string} role - Role name (doctor, finance, policy, hospital)
 * @param {string} bucket - Bucket value
 * @returns {Promise<Object>} - Result object
 */
export const updateRoleBucket = async (applicationDocId, role, bucket) => {
  try {
    const appRef = doc(db, 'patientApplications', applicationDocId);
    await updateDoc(appRef, {
      [`bucketFilter.${role}`]: bucket,
      'bucketFilter.updatedAt': Timestamp.now()
    });

    return {
      success: true,
      role,
      bucket
    };
  } catch (error) {
    console.error(`Error updating ${role} bucket filter:`, error);
    return {
      success: false,
      error: error.message
    };
  }
};

export default {
  determineDoctorBucket,
  determineFinanceBucket,
  determinePolicyBucket,
  determineHospitalBucket,
  calculateBucketFilter,
  updateBucketFilter,
  updateRoleBucket
};
